import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  ArrowLeftIcon, 
  BuildingOfficeIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { LeagueService, UserService } from '../../../services/firebaseService';

const schema = yup.object({
  name: yup.string().required('League name is required').min(3, 'League name must be at least 3 characters'),
  description: yup.string().optional(),
  maxTeams: yup.number().required('Max teams is required').min(1, 'Must allow at least 1 team').max(100, 'Cannot exceed 100 teams'),
  managerId: yup.string().optional()
}).required();

export default function CreateLeague() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      maxTeams: 10,
      managerId: ''
    }
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const usersData = await UserService.getAllUsers();
      // Filter users who can be managers (have league_manager or admin role)
      const managerUsers = usersData.filter(user => 
        user.roles && (
          user.roles.includes('league_manager') || 
          user.roles.includes('admin')
        )
      );
      setUsers(managerUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users for manager assignment');
    } finally {
      setLoadingUsers(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const leagueData = {
        name: data.name,
        description: data.description || '',
        maxTeams: data.maxTeams,
        status: 'active',
        currentTeams: 0
      };

      const result = await LeagueService.createLeague(leagueData, data.managerId || '');

      console.log('League created successfully:', result);
      setSuccess(true);
      
      // Redirect to leagues page after a short delay
      setTimeout(() => {
        navigate('/admin/leagues');
      }, 2000);

    } catch (error) {
      console.error('Error creating league:', error);
      setError(error.message || 'Failed to create league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateInvitationCode = () => {
    // Generate a random 8-character alphanumeric code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">League Created Successfully!</h2>
          <p className="text-gray-600 mb-4">The new league has been created and is ready for teams to join.</p>
          <p className="text-sm text-gray-500">Redirecting to Leagues page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin/leagues')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Create New League</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Set up a new league for players to join and compete
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">League Information</h2>
            </div>

            <div className="px-6 py-6">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* League Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    League Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    {...register('name')}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter league name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    {...register('description')}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter league description (optional)"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                {/* Max Teams */}
                <div>
                  <label htmlFor="maxTeams" className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Teams *
                  </label>
                  <input
                    type="number"
                    id="maxTeams"
                    min="1"
                    max="100"
                    {...register('maxTeams')}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                      errors.maxTeams ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter maximum number of teams"
                  />
                  {errors.maxTeams && (
                    <p className="mt-1 text-sm text-red-600">{errors.maxTeams.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Maximum number of teams that can join this league (1-100)
                  </p>
                </div>

                {/* Manager Assignment */}
                <div>
                  <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-2">
                    League Manager
                  </label>
                  {loadingUsers ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-survivor-600 mr-2"></div>
                      <span className="text-sm text-gray-500">Loading users...</span>
                    </div>
                  ) : (
                    <select
                      id="managerId"
                      {...register('managerId')}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500 ${
                        errors.managerId ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">No manager assigned</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.displayName} ({user.email})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.managerId && (
                    <p className="mt-1 text-sm text-red-600">{errors.managerId.message}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Assign a league manager to oversee this league (optional)
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/leagues')}
                    className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || loadingUsers}
                    className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-survivor-600 hover:bg-survivor-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      'Creating League...'
                    ) : (
                      <>
                        <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                        Create League
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
